import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import ModalHoi from 'components/ModalHoi';
import Context from 'managers/Context';
import { getLiteral } from 'utils/getLiteral';
import { createSender, updateSender } from 'services/SenderService';
import { successToast, errorToast } from 'utils/toast';
import { useSenderFormValidation } from './hooks/useSenderFormValidation';
import { useCountryOptions } from './hooks/useCountryOptions';
import GeoLocationModal from './GeoLocationModal';
import SenderFormFields from './SenderFormFields';

import './styles.scss';

const INITIAL_FORM_STATE = {
    name: '',
    senderEmail: '',
    responseEmail: '',
    nickName: '',
    address: '',
    addressDetails: '',
    city: '',
    region: '',
    postalCode: '',
    idCountry: null,
    country: '',
};

const SenderFormModal = ({ isOpen, onCancel, onRequestClose, onSuccess, senderToEdit }) => {
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [isGeoLocationModalOpen, setIsGeoLocationModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { countryOptions, countriesRaw } = useCountryOptions();

    const {
        errors,
        isFreeEmailDomain,
        updateFieldError,
        validateAndUpdateField,
        validateAllFields,
        clearErrors,
        setExternalErrors,
        clearFieldErrors,
        validateForm,
    } = useSenderFormValidation(formData);

    const searchLocations = useCallback((address) => {
        return Context.entityMapManager.autocomplete({ address });
    }, []);

    const getPlaceDetails = useCallback((placeId) => {
        return Context.entityMapManager.getPlaceDetails({ placeId });
    }, []);

    useEffect(() => {
        if (isOpen) return;
        setFormData(INITIAL_FORM_STATE);
        clearErrors();
        setIsSubmitting(false);
        setIsGeoLocationModalOpen(false);
    }, [isOpen, clearErrors]);

    useEffect(() => {
        if (!senderToEdit) return;
        setFormData({
            name: senderToEdit.name || '',
            senderEmail: senderToEdit.senderEmail || '',
            responseEmail: senderToEdit.responseEmail || '',
            nickName: senderToEdit.nickName || '',
            address: senderToEdit.address || '',
            addressDetails: senderToEdit.addressDetails || '',
            city: senderToEdit.city || '',
            region: senderToEdit.region || '',
            postalCode: senderToEdit.postalCode || '',
            idCountry: senderToEdit.idCountry || null,
            country: '',
        });
    }, [senderToEdit]);

    const handleFieldChange = useCallback(
        (field, value) => {
            setFormData((prev) => ({ ...prev, [field]: value }));
            updateFieldError(field, value);
        },
        [updateFieldError],
    );

    const handleCountryChange = useCallback(
        (value) => {
            const selectedOption = countryOptions.find((opt) => opt.value === value?.value);
            const newIdCountry = value?.value ? parseInt(value.value, 10) : null;

            setFormData((prev) => ({
                ...prev,
                idCountry: newIdCountry,
                country: selectedOption?.label || '',
            }));

            validateAndUpdateField('idCountry', newIdCountry);
        },
        [countryOptions, validateAndUpdateField],
    );

    const handleBlur = useCallback(
        (field) => {
            validateAndUpdateField(field, formData[field]);
        },
        [formData, validateAndUpdateField],
    );

    const handleClose = useCallback(() => {
        setFormData(INITIAL_FORM_STATE);
        clearErrors();
        setIsSubmitting(false);
        onCancel?.();
    }, [onCancel, clearErrors]);

    const isEditMode = useMemo(() => !!senderToEdit, [senderToEdit]);

    const isFormValid = useMemo(() => {
        const { isValid } = validateForm(formData);
        return isValid;
    }, [formData, validateForm]);

    const handleSubmit = useCallback(() => {
        const isValid = validateAllFields(formData);
        if (!isValid) return;

        setIsSubmitting(true);

        const payload = {
            name: formData.name,
            senderEmail: formData.senderEmail,
            responseEmail: formData.responseEmail,
            nickName: formData.nickName,
            address: formData.address,
            addressDetails: formData.addressDetails,
            city: formData.city,
            region: formData.region,
            postalCode: formData.postalCode,
            idCountry: formData.idCountry,
        };

        const action = isEditMode ? 'update' : 'create';
        const actionsConfig = {
            create: {
                promise: () => createSender(payload),
                successMessage: getLiteral('succes_entitycreatedsuccessfully'),
                errorMessage: getLiteral('label_failed_create'),
            },
            update: {
                promise: () => updateSender(senderToEdit.id, payload),
                successMessage: getLiteral('succes_entityupdatedsuccessfully'),
                errorMessage: getLiteral('label_failed_update'),
            },
        };
        const actionConfig = actionsConfig[action];

        actionConfig
            .promise()
            .then((responseData) => {
                successToast({
                    text: actionConfig.successMessage,
                });
                onSuccess?.(responseData);
                handleClose();
            })
            .catch((error) => {
                setExternalErrors(error?.data || {});
                errorToast({
                    text: actionConfig.errorMessage,
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [
        formData,
        isEditMode,
        senderToEdit,
        onSuccess,
        validateAllFields,
        handleClose,
        setExternalErrors,
    ]);

    const modalTitle = useMemo(
        () => (isEditMode ? getLiteral('label_edit_sender') : getLiteral('label_create_sender')),
        [isEditMode],
    );

    const confirmText = useMemo(
        () => (isEditMode ? getLiteral('action_save_changes') : getLiteral('action_create')),
        [isEditMode],
    );

    const openMapModal = useCallback(() => {
        setIsGeoLocationModalOpen(true);
    }, []);

    const closeMapModal = useCallback(() => {
        setIsGeoLocationModalOpen(false);
    }, []);

    const handleLocationSelected = useCallback(
        (locationData) => {
            setFormData((prev) => ({
                ...prev,
                address: locationData.address || prev.address,
                addressDetails: locationData.addressDetails,
                city: locationData.city,
                region: locationData.region,
                postalCode: locationData.postalCode,
                country: locationData.country || prev.country,
                idCountry: locationData.idCountry,
            }));

            const fieldsToClear = ['address', 'city', 'postalCode', 'idCountry'].filter(
                (field) => locationData[field],
            );
            clearFieldErrors(fieldsToClear);

            closeMapModal();
        },
        [clearFieldErrors, closeMapModal],
    );

    const countryValue = useMemo(() => {
        if (!formData.idCountry) return null;
        return countryOptions.find((opt) => opt.value === String(formData.idCountry)) || null;
    }, [countryOptions, formData.idCountry]);

    return (
        <>
            <ModalHoi
                className="marketing-sender-form-modal"
                isOpen={isOpen}
                onRequestClose={onRequestClose}
                onCancel={handleClose}
                onConfirm={handleSubmit}
                title={modalTitle}
                cancelText={getLiteral('action_cancel')}
                confirmText={confirmText}
                isLoading={isSubmitting}
                size="large"
                useHeader
                useCornerClose
                isConfirmDisabled={isSubmitting || !isFormValid}
            >
                <SenderFormFields
                    formData={formData}
                    errors={errors}
                    countryOptions={countryOptions}
                    countryValue={countryValue}
                    isFreeEmailDomain={isFreeEmailDomain}
                    onFieldChange={handleFieldChange}
                    onBlur={handleBlur}
                    onCountryChange={handleCountryChange}
                    onOpenMapModal={openMapModal}
                />
            </ModalHoi>

            <GeoLocationModal
                isOpen={isGeoLocationModalOpen}
                onCancel={closeMapModal}
                onConfirm={handleLocationSelected}
                initialAddress={formData.address}
                countriesRaw={countriesRaw}
                searchLocations={searchLocations}
                getPlaceDetails={getPlaceDetails}
            />
        </>
    );
};

SenderFormModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onCancel: PropTypes.func,
    onRequestClose: PropTypes.func,
    onSuccess: PropTypes.func,
    senderToEdit: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        senderEmail: PropTypes.string,
        responseEmail: PropTypes.string,
        nickName: PropTypes.string,
        address: PropTypes.string,
        addressDetails: PropTypes.string,
        city: PropTypes.string,
        region: PropTypes.string,
        postalCode: PropTypes.string,
        idCountry: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
};

export default memo(SenderFormModal);
